import LegalDisclaimer from './LegalDisclaimer';
import ResourcesModule from './ResourcesModule';
import OverviewModule from './OverviewModule';
import TextStyling from './TextStyling';
import Feature from './Feature';
import NotificationBanner from './NotificationBanner';

export enum POLICY_MODULE_SELECTOR_TAG_TEXT {
  IN_PROGRESS = 'IN PROGRESS',
  PENDING_ACTIVATION = 'PENDING ACTIVATION',
  ACTIVE = 'ACTIVE',
  ACTIVE_EXPIRING_SOON = 'EXPIRING SOON',
  CANCEL_REQUEST_SENT = 'CANCEL REQUEST SENT',
  CANCELED = 'CANCELED',
  DROPPED = 'DROPPED',
  OUTDATED = 'OUTDATED',
}

const EditAction = ({ id = '', label = '' } = {}) => ({
  id: String(id),
  label: String(label),
});

const Section = ({ id = '', title = {}, description = [] } = {}) => ({
  id: String(id),
  title: TextStyling(title),
  description: description.map((item) => TextStyling(item)),
});

export type SectionProps = ReturnType<typeof Section>;

export enum PendingStepIds {
  LEASE = 'lease',
  MANAGE_PAYMENTS = 'managePayment',
  PAY_FOR_POLICY = 'payForPolicy',
  SIGN_AND_PAY = 'signAndPay',
}

export const PendingStep = ({
  id = '',
  stepTitle = '',
  stepDescription = [],
  stepIcon = '',
  stepTagStatus = '',
  stepTagText = '',
  editAction = {},
  stepCta = {},
}: {
  id?: string;
  stepTitle?: string;
  // Parameters<typeof TextStyling>[0] is the type of the first argument of TextStyling
  // https://www.typescriptlang.org/docs/handbook/utility-types.html#parameterstype
  stepDescription?: Array<Parameters<typeof TextStyling>[0]>;
  stepIcon?: string;
  stepTagStatus?: string;
  stepTagText?: string;
  editAction?: Parameters<typeof EditAction>[0];
  stepCta?: Parameters<typeof TextStyling>[0];
} = {}) => ({
  id: String(id),
  stepTitle: String(stepTitle),
  stepDescription: stepDescription.map((item) => TextStyling(item)),
  stepIcon: String(stepIcon),
  stepTagStatus: String(stepTagStatus),
  stepTagText: String(stepTagText),
  editAction: EditAction(editAction),
  stepCta: TextStyling(stepCta),
});

export type PendingStepProps = ReturnType<typeof PendingStep>;

const PolicyDrop = ({
  id = '',
  dropType = '',
  message = '',
  actionLabel = '',
  actionLabelStyle = '',
  isSecurityDepositUpdateEligible = false,
} = {}) => ({
  id: String(id),
  dropType: String(dropType),
  message: String(message),
  actionLabel: String(actionLabel),
  actionLabelStyle: String(actionLabelStyle),
  isSecurityDepositUpdateEligible: Boolean(isSecurityDepositUpdateEligible),
});

export type PolicyDropProps = ReturnType<typeof PolicyDrop>;

const Selector = ({ description = {}, secondaryDescription = {} } = {}) => ({
  description: MultiPolicyDescription(description),
  secondaryDescription: MultiPolicyDescription(secondaryDescription),
});

const MultiPolicyDescription = ({ text = '' } = {}) => ({
  text: String(text),
});

export const PolicyModule = ({
  policyTitle = '',
  policyModuleType = '',
  tagText = '',
  tagStatus = '',
  securityDeposit = '',
  pendingSteps = [],
  shouldDisableAllSteps = false,
  policyDrop = {},
  pendingActivationSections = [],
  activeSections = [],
  actionLabels = [],
  isEffectivePolicyScreenDisplayed = false,
  cancelledSections = [],
  policyId = '',
  moduleId = '',
  infoMessage = [],
  premiumAmount = '',
  coverageAmount = '',
  selectedPremiumCoverageId = null,
  selector = {},
  shouldDisableClaimSubmission = false,
  description = [],
  notificationBanner = {},
  applicationId = '',
  manageActions = [],
  coverageEndDate = '',
  policyFeatures = [],
  rentPaymentsDashboardUrl = '',
  lastPossibleCoverageEndDate = '',
  tenants = [],
  cancelAction = [],
  isPremiumFullyPaid = false,
} = {}) => ({
  policyTitle: String(policyTitle),
  policyModuleType: String(policyModuleType),
  tagText: String(tagText),
  tagStatus: String(tagStatus),
  securityDeposit: String(securityDeposit),
  pendingSteps: pendingSteps.map((pendingStep) => PendingStep(pendingStep)),
  shouldDisableAllSteps: Boolean(shouldDisableAllSteps),
  policyDrop: PolicyDrop(policyDrop),
  pendingActivationSections: pendingActivationSections.map((section) => Section(section)),
  activeSections: activeSections.map((activeSection) => Section(activeSection)),
  actionLabels: actionLabels.map((actionLabel) => TextStyling(actionLabel)),
  isEffectivePolicyScreenDisplayed: Boolean(isEffectivePolicyScreenDisplayed),
  cancelledSections: cancelledSections.map((cancelledSection) => Section(cancelledSection)),
  policyId: String(policyId),
  moduleId: String(moduleId),
  infoMessage: infoMessage.map((item) => TextStyling(item)),
  premiumAmount: String(premiumAmount),
  coverageAmount: String(coverageAmount),
  selectedPremiumCoverageId: Number(selectedPremiumCoverageId),
  selector: Selector(selector),
  shouldDisableClaimSubmission: Boolean(shouldDisableClaimSubmission),
  description: description.map((desc) => TextStyling(desc)),
  notificationBanner: NotificationBanner(notificationBanner),
  applicationId: String(applicationId),
  manageActions: manageActions.map((manageAction) => TextStyling(manageAction)),
  coverageEndDate: String(coverageEndDate),
  policyFeatures: policyFeatures.map((feature) => Feature(feature)),
  rentPaymentsDashboardUrl: String(rentPaymentsDashboardUrl),
  lastPossibleCoverageEndDate: String(lastPossibleCoverageEndDate),
  tenants: tenants.map((tenant) => String(tenant)),
  cancelAction: cancelAction.map((cancelActionItem) => TextStyling(cancelActionItem)),
  isPremiumFullyPaid: Boolean(isPremiumFullyPaid),
});

export type PolicyModuleProps = ReturnType<typeof PolicyModule>;

const RentGuaranteeManagePolicyResponse = ({
  listingState = '',
  header = '',
  title = '',
  description = [],
  policyModules = [],
  resourcesModule = {},
  overviewModule = {},
  legalDisclaimer = {},
  notificationBanner = {},
} = {}) => ({
  listingState: String(listingState),
  header: String(header),
  title: String(title),
  description: description.map((item) => TextStyling(item)),
  policyModules: policyModules.map((policyModule) => PolicyModule(policyModule)),
  resourcesModule: ResourcesModule(resourcesModule),
  overviewModule: OverviewModule(overviewModule),
  legalDisclaimer: LegalDisclaimer(legalDisclaimer),
  notificationBanner: NotificationBanner(notificationBanner),
});

export default RentGuaranteeManagePolicyResponse;
